
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { configStore } from "@/store/store";
import { BuilderElement } from "@/types/elements";
import ElementStudioWidthAdjustmentBar from "./ElementStudioWidthAdjustmentBar.vue";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import ElementStudioTopbar from "./ElementStudioTopBar.vue";
import lodash from "lodash";

import {
  ElementStudioExpectedDataToLoad,
  CurrentlyEditingElement,
  ElementStudioUIMode,
  ElementStudioSaveLocation
} from "@/types/element-studio";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import defensiveCopy from "@/util/defensive-copy";
import ElementStudioTabsAndPanels from "@/components/ElementStudio/ElementStudioTabsAndPanels.vue";
import * as t from "io-ts";
import NotificationLogger from "@/lib/notifications/notifications";
import { updateElementAndPrecompileItsTemplates } from "@/lib/elements/utils";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";
import {
  getItemNamespaceFromSlug,
  getElementSlug,
  logNotificationOfSameItem
} from "@/lib/elements/studio/misc";
import { getElementWithoutExternalValues } from "@/lib/elements/studio/clean-element";
import {
  elementToPhp,
  getFinalDirectoryPath,
  removeElementPathFromDirectoryPath
} from "@/lib/elements/studio/element-to-php";
import { getBlankElement } from "@/lib/elements/studio/blank-element";

@Component({
  components: {
    ElementStudioTabsAndPanels,
    ElementStudioWidthAdjustmentBar,
    ElementStudioTopbar
  }
})
export default class ElementStudioElements extends Vue {
  @Prop()
  value!: ElementStudioExpectedDataToLoad;

  @Prop()
  width!: number;

  lastSavedElement: CurrentlyEditingElement | false = false;
  currentlyEditingElement: CurrentlyEditingElement | false = false;
  saveInProgress = false;

  get elementsSlug() {
    return this.value.elements.map(element => element.slug);
  }

  updateCurrentlyEditingElement(
    newCurrentlyEditingElement: CurrentlyEditingElement
  ) {
    this.currentlyEditingElement = defensiveCopy(newCurrentlyEditingElement);

    // Set the lastSavedElement when opening a new element
    if (this.lastSavedElement === false) {
      this.lastSavedElement = defensiveCopy(newCurrentlyEditingElement);
    }
  }

  updateAvailableElementsAndStore(
    newCurrentlyEditingElement: CurrentlyEditingElement
  ) {
    const newSlug = newCurrentlyEditingElement.element.slug;

    // Delete the old element if the user is changing the slug
    // and it's not a copy of the element in a different save location
    if (
      this.lastSavedElement &&
      newSlug !== this.lastSavedElement.element.slug &&
      getItemNamespaceFromSlug(newSlug) ===
        getItemNamespaceFromSlug(this.lastSavedElement.element.slug)
    ) {
      this.deleteElementFromServer(this.lastSavedElement);
    }

    const existingElement = !!this.value.elements.find(
      element => element.slug === newSlug
    );

    if (!existingElement && newCurrentlyEditingElement.directoryPath) {
      const newValue = defensiveCopy(this.value);

      newValue.elements.push({
        slug: newSlug,
        directoryPath: newCurrentlyEditingElement.directoryPath
      });

      this.$emit("input", newValue);
    }

    this.lastSavedElement = newCurrentlyEditingElement;
  }

  get hasUnsavedChanges() {
    if (!this.currentlyEditingElement || !this.lastSavedElement) {
      return false;
    }

    return !lodash.isEqual(
      this.lastSavedElement.element,
      this.currentlyEditingElement.element
    );
  }

  // throttle to make the experience smoother.
  // There's lot of stuff running after an element updates
  throttledUpdateElement = lodash.throttle(
    updateElementAndPrecompileItsTemplates,
    500
  );

  updateData(newData: ElementStudioExpectedDataToLoad) {
    this.$emit("input", newData);
  }

  updateElement(newElement: BuilderElement) {
    if (
      !this.currentlyEditingElement ||
      !this.lastSavedElement ||
      !this.lastSavedElement.element
    ) {
      return;
    }

    const slugChanged = newElement.slug !== this.lastSavedElement.element.slug;

    let directoryPath = this.currentlyEditingElement.directoryPath;

    if (slugChanged && this.currentlyEditingElement.directoryPath) {
      directoryPath = getFinalDirectoryPath({
        directoryPath: removeElementPathFromDirectoryPath(
          this.currentlyEditingElement.directoryPath
        ),
        elementName: this.currentlyEditingElement.element.name
      });
    }

    this.currentlyEditingElement = {
      directoryPath,
      element: newElement,
      onlyForAdvancedUsers: this.currentlyEditingElement.onlyForAdvancedUsers
    };

    this.throttledUpdateElement(newElement);
  }

  deleteElementFromStore(elementSlugToDelete: string) {
    const indexOfElementToDelete = this.value.elements.findIndex(
      element => element.slug === elementSlugToDelete
    );

    configStore.deleteElement(elementSlugToDelete);

    if (indexOfElementToDelete !== -1) {
      this.value.elements.splice(indexOfElementToDelete, 1);
    }
  }

  deleteElement() {
    if (!this.currentlyEditingElement) {
      return;
    }

    this.deleteElementFromServer(this.currentlyEditingElement, () => {
      this.currentlyEditingElement = false;
    });
  }

  async deleteElementFromServer(
    elementToDelete: CurrentlyEditingElement,
    successCallback: () => void = () => {}
  ) {
    if (!elementToDelete.element) return;

    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_delete_element",
        dataToPost: {
          directoryPath: elementToDelete.directoryPath
        },
        decoder: t.literal("success"),
        resultKey: "data"
      }),
      fold(
        () => {
          NotificationLogger.log.message("Failed to delete element");
        },
        () => {
          if (!elementToDelete.element) {
            NotificationLogger.log.message("Failed to delete element");

            return;
          }

          this.deleteElementFromStore(elementToDelete.element.slug);
          successCallback();
        }
      )
    );
  }

  discardChanges() {
    if (this.lastSavedElement && this.lastSavedElement.element) {
      // Update to the last saved state
      this.updateElement(this.lastSavedElement.element);
    }

    this.resetCurrentlyEditingElement();
  }

  resetCurrentlyEditingElement() {
    this.currentlyEditingElement = false;
    this.lastSavedElement = false;
    this.$emit("active", null);
  }

  /* ui stuff */
  mode: ElementStudioUIMode =
    (readFromLocalStorage("element-studio-mode") as ElementStudioUIMode) ||
    "normal";

  switchMode() {
    switch (this.mode) {
      case "split-vertical":
        this.mode = "split-horizontal";
        this.$emit("width", 750);
        break;
      case "split-horizontal":
        this.$emit("width", 600);
        this.mode = "normal";
        break;
      case "normal":
        this.$emit("width", 600);
        this.mode = "split-vertical";
        break;
    }

    writeToLocalStorage("element-studio-mode", this.mode);
  }

  openElement(elementSlug: string) {
    const element = defensiveCopy(configStore.element(elementSlug));
    const newStudioElement = this.value.elements.find(
      element => element.slug === elementSlug
    );

    if (!newStudioElement) return;

    const activeSaveLocation = this.value.saveLocations.find(saveLocation =>
      newStudioElement.directoryPath.startsWith(saveLocation.directoryPath)
    );
    const onlyForAdvancedUsers = !!activeSaveLocation?.onlyForAdvancedUsers;

    this.updateCurrentlyEditingElement({
      element,
      directoryPath: newStudioElement.directoryPath,
      onlyForAdvancedUsers
    });

    this.$emit("active", "element");
  }

  saveAs(saveLocation: ElementStudioSaveLocation) {
    if (!this.currentlyEditingElement) return;

    const { namespace, directoryPath, onlyForAdvancedUsers } = saveLocation;

    const currentElement = defensiveCopy(this.currentlyEditingElement.element);

    // force the slug to be recalculated when saving on a new location
    if (this.currentlyEditingElement.directoryPath !== directoryPath) {
      currentElement.slug = "";
    }

    this.saveToServer(
      currentElement,
      getFinalDirectoryPath({
        directoryPath,
        elementName: this.currentlyEditingElement.element.name
      }),
      namespace,
      false,
      onlyForAdvancedUsers
    );
  }

  saveNewItem(newItem: ElementStudioSaveLocation & { name: string }) {
    const newSlug = getElementSlug(newItem.namespace, newItem.name);

    if (this.value.elements.find(element => element.slug === newSlug)) {
      logNotificationOfSameItem(this.value.saveLocations, newItem, "element");

      return;
    }

    const newElement = {
      ...getBlankElement(),
      slug: newSlug,
      name: newItem.name
    };

    this.saveToServer(
      newElement,
      getFinalDirectoryPath({
        directoryPath: newItem.directoryPath,
        elementName: newItem.name
      }),
      newItem.namespace,
      false,
      newItem.onlyForAdvancedUsers
    );

    this.$emit("active", "element");
  }

  saveOverride(bypassPsalm: boolean) {
    if (
      !this.currentlyEditingElement ||
      !this.currentlyEditingElement.directoryPath
    ) {
      return;
    }

    this.saveToServer(
      this.currentlyEditingElement.element,
      this.currentlyEditingElement.directoryPath,
      getItemNamespaceFromSlug(this.currentlyEditingElement.element.slug),
      bypassPsalm,
      this.currentlyEditingElement.onlyForAdvancedUsers
    );
  }

  async saveToServer(
    element: BuilderElement,
    directoryPath: string,
    namespaceArg: string | null,
    bypassPsalm = false,
    onlyForAdvancedUsers: boolean
  ) {
    this.saveInProgress = true;

    const namespace = namespaceArg || getItemNamespaceFromSlug(element.slug);
    const cleanElement = getElementWithoutExternalValues(element);
    const elementSlug = element.slug || getElementSlug(namespace, element.name);

    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_save_element",
        dataToPost: {
          ...elementToPhp(cleanElement, namespace),
          directoryPath,
          bypassPsalm: bypassPsalm ? "yes" : "no"
        },
        decoder: t.unknown
      }),
      fold(
        e => {
          console.error(e);
          NotificationLogger.log.message(
            "Failed to save element, check console"
          );
        },
        () => {
          const newElement = {
            element: {
              ...defensiveCopy(element),
              slug: elementSlug,
              // Uppercase first letters for consistency in naming across elements
              name: element.name
                .split(" ")
                .map(word => word.charAt(0).toUpperCase() + word.substring(1))
                .join(" ")
            },
            directoryPath,
            onlyForAdvancedUsers
          };

          this.updateCurrentlyEditingElement(newElement);
          this.updateAvailableElementsAndStore(newElement);
          updateElementAndPrecompileItsTemplates(newElement.element);
        }
      )
    );

    this.saveInProgress = false;
  }
}
